import {
  type InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  getNotificationCount,
  getNotificationCounts,
  getNotifications,
  markNotificationsSeen,
  resolveNotification,
  resolveSubjectNotifications,
} from '@/lib/apiClient';
import type {
  NotificationListResult,
  SubjectType,
} from '@/types/notifications';
import { cappedRefetchInterval } from '@/lib/referralPolling';

/**
 * Query keys for the notification center. Kept distinct from every other key in
 * the app (and never shared between useQuery and useInfiniteQuery).
 */
export const notificationKeys = {
  count: ['notificationsCount'] as const,
  feed: ['notificationsFeed'] as const,
  subject: (subjectType: SubjectType, subjectId: string) =>
    ['subjectNotifications', subjectType, subjectId] as const,
  subjectCounts: (subjectType: SubjectType, subjectIds: string[]) =>
    ['notificationCounts', subjectType, subjectIds] as const,
  // Distinct prefix: this is a useInfiniteQuery ({pages,pageParams}); never
  // share it with the useQuery feed key above, which holds a flat {items}.
  history: (filter: NotificationHistoryFilter) =>
    ['notificationsHistory', filter] as const,
};

const COUNT_POLL_INTERVAL_MS = 60_000;
// Bounded poll (per CODING_STANDARDS.md): stop after this window on a single
// mount so a focused, abandoned admin tab doesn't fire COUNT queries forever.
// The query still refetches on window focus and after mutations.
const COUNT_POLL_CAP_MS = 30 * 60_000;

/** Badge counts (open + new-since-seen). Polled, capped, background-paused by default. */
export function useNotificationCount(options?: { enabled?: boolean }) {
  const startedAt = useRef<number | null>(null);
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: notificationKeys.count,
    queryFn: () => getNotificationCount(),
    enabled,
    refetchInterval: () =>
      cappedRefetchInterval(
        enabled,
        startedAt,
        COUNT_POLL_INTERVAL_MS,
        COUNT_POLL_CAP_MS
      ),
  });
}

/** The global center feed (open notifications, newest first). */
export function useNotificationFeed(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: notificationKeys.feed,
    queryFn: () => getNotifications({ status: 'open', limit: 20 }),
    enabled: options?.enabled ?? true,
  });
}

/** Filter for the full-history page: everything, or one lifecycle slice. */
export type NotificationHistoryFilter = 'all' | 'open' | 'resolved';

const HISTORY_PAGE_SIZE = 30;

/**
 * The full notification history (all subjects, both open and resolved),
 * cursor-paginated for infinite scroll on the dedicated /notifications page.
 * `all` omits the status filter so open and resolved interleave newest-first.
 */
export function useNotificationHistory(filter: NotificationHistoryFilter) {
  return useInfiniteQuery({
    queryKey: notificationKeys.history(filter),
    queryFn: ({ pageParam }) =>
      getNotifications({
        status: filter === 'all' ? undefined : filter,
        limit: HISTORY_PAGE_SIZE,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
  });
}

/** Open notifications for a single workflow (the per-workflow panel). */
export function useSubjectNotifications(
  subjectType: SubjectType,
  subjectId: string | undefined
) {
  return useQuery({
    queryKey: notificationKeys.subject(subjectType, subjectId ?? ''),
    queryFn: () =>
      getNotifications({
        status: 'open',
        subjectType,
        subjectId: subjectId!,
      }),
    enabled: !!subjectId,
  });
}

/** Page-scoped open counts for a set of records (the list-row badges). */
export function useSubjectNotificationCounts(
  subjectType: SubjectType,
  subjectIds: string[],
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: notificationKeys.subjectCounts(subjectType, subjectIds),
    queryFn: () => getNotificationCounts(subjectType, subjectIds),
    enabled: (options?.enabled ?? true) && subjectIds.length > 0,
  });
}

/**
 * Invalidate every notification query so the badge, center feed, and per-row
 * pills refetch. Exported so producers of notifications (e.g. the fax-link
 * mutation) can refresh the linker's own view immediately instead of waiting for
 * the next count poll.
 */
export function invalidateNotificationQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['notificationsCount'] });
  queryClient.invalidateQueries({ queryKey: ['notificationsFeed'] });
  queryClient.invalidateQueries({ queryKey: ['subjectNotifications'] });
  queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
  queryClient.invalidateQueries({ queryKey: ['notificationsHistory'] });
}

function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  return useCallback(
    () => invalidateNotificationQueries(queryClient),
    [queryClient]
  );
}

const dropItem = (id: string) => (old?: NotificationListResult) =>
  old ? { ...old, items: old.items.filter(item => item.id !== id) } : old;

/**
 * Optimistically reflect a resolve in a history (useInfiniteQuery) cache. The
 * 'open' slice drops the row; 'all'/'resolved' flip it to resolved in place so
 * it renders as a muted "Resolved" row without leaving its position.
 */
function applyResolveToHistory(
  data: InfiniteData<NotificationListResult> | undefined,
  id: string,
  filter: NotificationHistoryFilter,
  resolvedAt: string
): InfiniteData<NotificationListResult> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map(page =>
      filter === 'open'
        ? { ...page, items: page.items.filter(item => item.id !== id) }
        : {
            ...page,
            items: page.items.map(item =>
              item.id === id
                ? { ...item, resolvedAt: item.resolvedAt ?? resolvedAt }
                : item
            ),
          }
    ),
  };
}

/**
 * Resolve a single notification. Optimistically updates every surface — the
 * feed, per-referral panels, and the history list — rolling back (and toasting)
 * on failure. The history caches are marked stale without an immediate refetch
 * (the optimistic edit already shows the right state), so one resolve doesn't
 * re-pull every loaded page of the /notifications list.
 */
export function useResolveNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['notification-resolve'],
    mutationFn: (id: string) => resolveNotification(id),
    onMutate: async (id: string) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['notificationsFeed'] }),
        queryClient.cancelQueries({ queryKey: ['subjectNotifications'] }),
        queryClient.cancelQueries({ queryKey: ['notificationsHistory'] }),
      ]);
      const previous = [
        ...queryClient.getQueriesData({ queryKey: ['notificationsFeed'] }),
        ...queryClient.getQueriesData({ queryKey: ['subjectNotifications'] }),
        ...queryClient.getQueriesData({ queryKey: ['notificationsHistory'] }),
      ];
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ['notificationsFeed'] },
        dropItem(id)
      );
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ['subjectNotifications'] },
        dropItem(id)
      );
      // History is paginated ({pages,pageParams}); update it per-filter using
      // each cache's own filter (the 2nd key segment), which a single
      // setQueriesData updater can't see.
      const resolvedAt = new Date().toISOString();
      for (const [key, data] of queryClient.getQueriesData<
        InfiniteData<NotificationListResult>
      >({ queryKey: ['notificationsHistory'] })) {
        const filter = key[1] as NotificationHistoryFilter;
        queryClient.setQueryData(
          key,
          applyResolveToHistory(data, id, filter, resolvedAt)
        );
      }
      return { previous };
    },
    onError: (_error, _id, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData(key, data)
      );
      toast.error('Failed to resolve notification');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationsCount'] });
      queryClient.invalidateQueries({ queryKey: ['notificationsFeed'] });
      queryClient.invalidateQueries({ queryKey: ['subjectNotifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
      // Already optimistically patched above — mark stale but don't refetch
      // now; inactive filter slices (e.g. 'resolved') refresh when next viewed.
      queryClient.invalidateQueries({
        queryKey: ['notificationsHistory'],
        refetchType: 'none',
      });
    },
  });
}

/** Resolve every open notification for a record ("Resolve all"). */
export function useResolveSubjectNotifications() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationKey: ['subject-notifications-resolve'],
    mutationFn: ({
      subjectType,
      subjectId,
    }: {
      subjectType: SubjectType;
      subjectId: string;
    }) => resolveSubjectNotifications(subjectType, subjectId),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to resolve notifications'),
  });
}

/** Mark all open notifications as seen (de-bolds for the user; never resolves). */
export function useMarkNotificationsSeen() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationKey: ['notifications-mark-seen'],
    mutationFn: () => markNotificationsSeen(),
    onSuccess: invalidate,
  });
}
